// Step 1 of My style: the pasted emails, the short answers and the optional AI-draft pair.
// Controlled by the page; saving sends the whole form as a replace. Once something is saved the
// form folds away, so Scout's asks below are what the strategist sees next (P4-08).

import type { StyleEmail, StyleSituation } from '../../api/types';
import { labelLine, STYLE_LENGTHS, STYLE_SITUATIONS } from '../../lib/styleIntake';
import type { StyleForm, StyleFormEmail } from '../../lib/styleIntake';

// The server's own cap (`voice_profile.MAX_EMAILS`), so the page never offers a box it refuses.
const MAX_EMAILS = 16;

export function StyleIntakeForm({
  form,
  emails,
  onChange,
  busy,
  saved,
  onCheck,
}: {
  form: StyleForm;
  // What the server stored and labelled, by position, for the line under each box.
  emails: StyleEmail[];
  onChange: (form: StyleForm) => void;
  busy: boolean;
  // Emails are already stored: the form is for edits, folded by default.
  saved: boolean;
  onCheck: () => void;
}) {
  function setEmail(index: number, change: Partial<StyleFormEmail>) {
    const next = form.emails.map((email, position) => (position === index ? { ...email, ...change } : email));
    onChange({ ...form, emails: next });
  }

  function removeEmail(index: number) {
    onChange({ ...form, emails: form.emails.filter((_, position) => position !== index) });
  }

  function addEmail() {
    onChange({ ...form, emails: [...form.emails, { text: '', situationHint: '' }] });
  }

  const filledCount = form.emails.filter((email) => email.text.trim()).length;
  const body = (
    <>
      <h3>Your emails</h3>
      <p className="stylehint">
        One email per box, as you sent it. A mix helps: cold, follow-up, playground user, warm
        reply, re-engage and meeting, to a few kinds of buyer. Scout sorts them; pick a type only
        if you want to.
      </p>
      {form.emails.map((email, index) => (
        <div className="styleemail" key={index}>
          <div className="styleemailhead">
            <span className="styleemailnum">Email {index + 1}</span>
            <select
              value={email.situationHint}
              onChange={(event) => setEmail(index, { situationHint: event.target.value as StyleSituation | '' })}
              aria-label={`Situation for email ${index + 1}`}
            >
              <option value="">Scout decides</option>
              {STYLE_SITUATIONS.map((situation) => (
                <option key={situation.key} value={situation.key}>{situation.label}</option>
              ))}
            </select>
            <button type="button" className="btn" onClick={() => removeEmail(index)}>Remove</button>
          </div>
          <textarea
            rows={6}
            value={email.text}
            onChange={(event) => setEmail(index, { text: event.target.value })}
            placeholder="Paste the email, greeting to sign-off."
          />
          {emails[index] && emails[index].text === email.text.trim() ? (
            <p className="stylelabel">{labelLine(emails[index].label)}</p>
          ) : null}
        </div>
      ))}
      {form.emails.length < MAX_EMAILS ? (
        <button type="button" className="btn" onClick={addEmail}>Add another email</button>
      ) : null}

      <h3>A few answers</h3>
      <label className="stylefield">
        How do you want to sound?
        <textarea rows={2} value={form.howYouSound} onChange={(event) => onChange({ ...form, howYouSound: event.target.value })} />
      </label>
      <div className="stylegrid">
        <label className="stylefield">
          Phrases that are yours (comma-separated)
          <input value={form.always} onChange={(event) => onChange({ ...form, always: event.target.value })} />
        </label>
        <label className="stylefield">
          Phrases you never want
          <input value={form.never} onChange={(event) => onChange({ ...form, never: event.target.value })} />
        </label>
        <label className="stylefield">
          Greeting (Hi, Hey, name only, none)
          <input value={form.greeting} onChange={(event) => onChange({ ...form, greeting: event.target.value })} />
        </label>
        <label className="stylefield">
          Sign-off
          <input value={form.signOff} onChange={(event) => onChange({ ...form, signOff: event.target.value })} />
        </label>
        <label className="stylefield">
          Your default ask
          <input
            list="style-asks"
            value={form.defaultAsk}
            onChange={(event) => onChange({ ...form, defaultAsk: event.target.value })}
          />
          <datalist id="style-asks">
            <option value="A meeting" />
            <option value="A quick question" />
            <option value="Offer test credits" />
            <option value="Send a resource" />
          </datalist>
        </label>
        <label className="stylefield">
          Length
          <select value={form.length} onChange={(event) => onChange({ ...form, length: event.target.value as StyleForm['length'] })}>
            <option value="">Choose…</option>
            {STYLE_LENGTHS.map((length) => <option key={length.key} value={length.key}>{length.label}</option>)}
          </select>
        </label>
      </div>

      <details className="styleoptional">
        <summary>Optional: an AI draft and how you rewrote it</summary>
        <div className="stylegrid">
          <label className="stylefield">
            The AI draft
            <textarea rows={6} value={form.aiDraft} onChange={(event) => onChange({ ...form, aiDraft: event.target.value })} />
          </label>
          <label className="stylefield">
            What you sent instead
            <textarea rows={6} value={form.rewrite} onChange={(event) => onChange({ ...form, rewrite: event.target.value })} />
          </label>
        </div>
      </details>

      <button type="button" className={saved ? 'btn' : 'btn primary'} disabled={busy || !filledCount} onClick={onCheck}>
        {busy ? 'Scout is reading your emails…' : saved ? 'Save changes' : 'Save and check'}
      </button>
    </>
  );

  if (!saved) return <section className="stylestep">{body}</section>;
  return (
    <details className="stylestep stylefolded">
      <summary>Your emails and answers ({filledCount} saved) · edit</summary>
      {body}
    </details>
  );
}
